
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ATTACHMENTS_BUCKET_NAME } from './constants';
import { useBucketManagement } from './useBucketManagement';
import { TransactionAttachment, AttachmentForm, AttachmentType } from '@/types/transaction';

export interface FileWithDescription {
  file: File;
  description: string;
  attachment_form?: AttachmentForm;
  attachment_type?: AttachmentType;
}

export function useFileOperations() {
  const { createBucketIfNotExists } = useBucketManagement();

  // Buscar anexos de uma transação
  const getTransactionAttachments = useCallback(async (transactionId: string) => {
    try {
      console.log('🔍 getTransactionAttachments - Buscando anexos para transação:', transactionId);

      const { data: attachments, error } = await supabase
        .from('transaction_attachments')
        .select('*')
        .eq('transaction_id', transactionId);

      if (error) {
        console.error('❌ getTransactionAttachments - Erro ao buscar anexos:', error);
        console.error('   Código:', error.code);
        console.error('   Mensagem:', error.message);
        throw error;
      }

      // Verificar se existem anexos
      if (!attachments || attachments.length === 0) {
        console.log('📎 getTransactionAttachments - Nenhum anexo encontrado para transação:', transactionId);
        return [];
      }

      console.log(`📎 getTransactionAttachments - ${attachments.length} anexos encontrados para transação ${transactionId}:`);
      attachments.forEach((a, index) => {
        console.log(`   ${index + 1}. ID: ${a.id}, Arquivo: ${a.file_name}, Path: ${a.file_path}`);
      });
      
      // Garantir que o bucket existe
      const bucketResult = await createBucketIfNotExists(ATTACHMENTS_BUCKET_NAME);
      
      if (!bucketResult.success) {
        console.error('Erro ao criar/verificar bucket:', bucketResult.error);
        toast.error('Erro ao acessar o armazenamento de anexos');
      }

      // Obter URLs públicas para cada anexo
      const attachmentsWithUrls = await Promise.all(
        attachments.map(async (attachment) => {
          try {
            // Verificar se o caminho do arquivo é válido
            if (!attachment.file_path) {
              console.error('Caminho de arquivo inválido para anexo:', attachment.id);
              return {
                ...attachment,
                url: null,
                error: 'Caminho de arquivo inválido'
              };
            }

            console.log(`Tentando obter URL para anexo ${attachment.id} com caminho: ${attachment.file_path}`);

            // Obter URL pública diretamente
            const { data } = supabase
              .storage
              .from(ATTACHMENTS_BUCKET_NAME)
              .getPublicUrl(attachment.file_path);

            console.log(`URL para anexo ${attachment.id}:`, data.publicUrl);

            return {
              ...attachment,
              url: data.publicUrl,
              error: null
            };
          } catch (urlError) {
            console.error(`Erro ao obter URL para anexo ${attachment.id}:`, urlError);
            return {
              ...attachment,
              url: null,
              error: urlError
            };
          }
        })
      );

      return attachmentsWithUrls;
    } catch (error) {
      console.error('Erro em getTransactionAttachments:', error);
      toast.error('Erro ao carregar os anexos da transação');
      return [];
    }
  }, [createBucketIfNotExists]);

  // Upload de anexos
  const uploadAttachments = useCallback(async (userId: string, transactionId: string, filesWithDescriptions: FileWithDescription[]) => {
    try {
      console.log(`Iniciando upload de ${filesWithDescriptions.length} anexos para transação ${transactionId}`);
      
      // Validação inicial
      if (!userId || !transactionId) {
        throw new Error('ID de usuário ou transação não fornecido');
      }
      
      if (!filesWithDescriptions || filesWithDescriptions.length === 0) {
        console.log('Nenhum arquivo para upload');
        return [];
      }
      
      // Garantir que o bucket existe - fazer isso apenas uma vez no início
      const bucketResult = await createBucketIfNotExists(ATTACHMENTS_BUCKET_NAME);
      
      if (!bucketResult.success) {
        console.error(`Erro ao criar/verificar bucket ${ATTACHMENTS_BUCKET_NAME}:`, bucketResult.error);
        toast.error('Erro ao preparar armazenamento para anexos');
        throw new Error(`Erro ao acessar bucket ${ATTACHMENTS_BUCKET_NAME}: ${bucketResult.error}`);
      }
      
      const uploadResults = [];
      let successCount = 0;
      let failureCount = 0;

      // Processar arquivos em sequência para evitar problemas de concorrência
      for (let index = 0; index < filesWithDescriptions.length; index++) {
        const { file, description } = filesWithDescriptions[index];
        
        try {
          // Validação do arquivo
          if (!file || !(file instanceof File)) {
            console.error(`Arquivo inválido no índice ${index}`);
            failureCount++;
            continue;
          }
          
          // Criar caminho único usando userId e transactionId com timestamp para evitar colisões
          const timestamp = Date.now() + index; // Adicionar index para garantir unicidade
          const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const filePath = `${userId}/${transactionId}/${timestamp}_${sanitizedName}`;

          console.log(`[${index + 1}/${filesWithDescriptions.length}] Fazendo upload do arquivo ${file.name} para caminho: ${filePath}`);

          // Upload do arquivo para o storage
          const { error: uploadError } = await supabase.storage
            .from(ATTACHMENTS_BUCKET_NAME)
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: true
            });

          if (uploadError) {
            console.error('Erro ao fazer upload do arquivo:', uploadError);
            
            // Tratamento específico para erros comuns
            if (uploadError.message?.includes('Bucket not found')) {
              toast.error('Bucket de anexos não encontrado. Tente novamente.');
            } else if (uploadError.message?.includes('File size exceeds')) {
              toast.error(`Arquivo ${file.name} excede o tamanho limite`);
            } else if (uploadError.message?.includes('Invalid file type')) {
              toast.error(`Tipo de arquivo inválido: ${file.type}`);
            } else {
              toast.error(`Erro ao fazer upload do arquivo ${file.name}: ${uploadError.message}`);
            }
            
            failureCount++;
            continue;
          }

          // Obter URL pública para o arquivo enviado
          const { data: urlData } = supabase
            .storage
            .from(ATTACHMENTS_BUCKET_NAME)
            .getPublicUrl(filePath);

          console.log(`URL pública para ${file.name}:`, urlData.publicUrl);

          // Registrar anexo na tabela transaction_attachments com descrição
          const { data: attachmentData, error: attachmentError } = await supabase
            .from('transaction_attachments')
            .insert({
              transaction_id: transactionId,
              file_name: file.name,
              file_path: filePath,
              file_type: file.type || 'application/octet-stream',
              description: description || null,
            })
            .select()
            .single();

          if (attachmentError) {
            console.error('Erro ao registrar anexo:', attachmentError);
            
            // Tratamento específico para erros de banco
            if (attachmentError.code === '23505') { // Unique violation
              toast.error(`Arquivo ${file.name} já foi anexado a esta transação`);
            } else if (attachmentError.code === '23503') { // Foreign key violation
              toast.error('Transação não encontrada. Não foi possível anexar o arquivo.');
            } else {
              toast.error(`Erro ao salvar informações do arquivo ${file.name}: ${attachmentError.message}`);
            }
            
            failureCount++;
            continue;
          }

          uploadResults.push({
            ...attachmentData,
            url: urlData.publicUrl
          });
          
          successCount++;
          console.log(`✅ Anexo ${file.name} processado com sucesso`);

        } catch (fileError) {
          console.error(`Erro inesperado ao processar arquivo ${file?.name}:`, fileError);
          toast.error(`Erro inesperado ao processar arquivo ${file?.name || 'desconhecido'}`);
          failureCount++;
        }
      }

      // Feedback final
      if (filesWithDescriptions.length === 1) {
        if (successCount === 1) {
          toast.success(`Arquivo ${filesWithDescriptions[0].file.name} anexado com sucesso`);
        } else {
          toast.error('Não foi possível anexar o arquivo');
        }
      } else {
        if (failureCount === 0) {
          toast.success(`${successCount} arquivos anexados com sucesso`);
        } else if (successCount > 0) {
          toast.warning(`${successCount} arquivos anexados, ${failureCount} falharam`);
        } else {
          toast.error('Nenhum arquivo pôde ser anexado');
        }
      }

      return uploadResults;
    } catch (error) {
      console.error('Erro em uploadAttachments:', error);
      
      // Tratamento de erro geral
      if (error instanceof Error) {
        toast.error(`Erro ao processar anexos: ${error.message}`);
      } else {
        toast.error('Erro desconhecido ao processar anexos');
      }
      
      throw error;
    }
  }, [createBucketIfNotExists]);

  // Excluir anexo
  const deleteAttachment = useCallback(async (attachment: TransactionAttachment | { file_path: string; id: string | number }) => {
    const filePath = 'file_path' in attachment ? attachment.file_path : attachment.file_path;
    const attachmentId = 'id' in attachment ? String(attachment.id) : attachment.id;
    try {
      console.log(`Tentando excluir arquivo: ${filePath}, attachmentId: ${attachmentId}`);
      
      // Verificar se o bucket existe antes de tentar excluir
      const bucketResult = await createBucketIfNotExists(ATTACHMENTS_BUCKET_NAME);
      
      if (!bucketResult.success) {
        console.error('Erro ao verificar bucket para exclusão de arquivo:', bucketResult.error);
        toast.warning('Aviso: Bucket não disponível, apenas removendo registro do anexo');
        
        // Mesmo sem bucket, excluir registro do banco de dados
        const { error: dbError } = await supabase
          .from('transaction_attachments')
          .delete()
          .eq('id', attachmentId);
  
        if (dbError) {
          console.error('Erro ao excluir registro de anexo:', dbError);
          toast.error('Erro ao excluir registro de anexo');
          throw dbError;
        }
        
        toast.success('Registro de anexo removido, mas o arquivo pode permanecer no storage');
        return;
      }
      
      // Excluir arquivo do storage
      const { error: storageError } = await supabase.storage
        .from(ATTACHMENTS_BUCKET_NAME)
        .remove([filePath]);

      if (storageError) {
        console.error('Erro ao excluir arquivo do storage:', storageError);
        toast.warning('Aviso: Não foi possível excluir o arquivo, apenas o registro');
      }

      // Excluir registro da tabela transaction_attachments
      const { error: dbError } = await supabase
        .from('transaction_attachments')
        .delete()
        .eq('id', attachmentId);

      if (dbError) {
        console.error('Erro ao excluir registro de anexo:', dbError);
        toast.error('Erro ao excluir registro de anexo');
        throw dbError;
      }

      toast.success('Anexo removido com sucesso');
    } catch (error) {
      console.error('Erro em deleteAttachment:', error);
      toast.error('Erro ao remover anexo');
      throw error;
    }
  }, [createBucketIfNotExists]);

  // Download de arquivo
  const downloadAttachment = useCallback(async (attachment: { file_path: string; file_name: string; url?: string | null }) => {
    try {
      console.log('Tentando baixar anexo:', attachment);
      
      // Garantir que o bucket existe
      const bucketResult = await createBucketIfNotExists(ATTACHMENTS_BUCKET_NAME);
      
      if (!bucketResult.success) {
        console.error('Erro ao verificar bucket para download:', bucketResult.error);
        toast.error('Erro ao baixar arquivo: bucket não disponível');
        return;
      }
      
      if (attachment.url) {
        console.log('URL disponível, iniciando download via URL pública:', attachment.url);
        
        // Download via URL pública se disponível
        const link = document.createElement('a');
        link.href = attachment.url;
        link.download = attachment.file_name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Download de ${attachment.file_name} iniciado`);
      } else {
        console.log('URL não disponível, fazendo download via Supabase Storage');
        
        // Download direto via API do Storage
        const { data, error } = await supabase.storage
          .from(ATTACHMENTS_BUCKET_NAME)
          .download(attachment.file_path);
          
        if (error) {
          console.error('Erro ao baixar arquivo:', error);
          toast.error(`Erro ao baixar ${attachment.file_name}`);
          return;
        }
        
        // Criar URL de blob e iniciar download
        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`Download de ${attachment.file_name} concluído`);
      }
    } catch (error) {
      console.error('Erro ao fazer download do anexo:', error);
      toast.error('Falha ao fazer download do arquivo');
    }
  }, [createBucketIfNotExists]);

  return {
    getTransactionAttachments,
    uploadAttachments,
    deleteAttachment,
    downloadAttachment
  };
}
